import { CHANNEL_TYPES, CONNECTOR_TYPES, PROVIDER_TYPES } from '@/vars/general'

const LS_FIRST_BOT_PREFIX = 'dh_subscribers_growth_first_bot_ms_'

// Chat-bot channels for this widget: MAX Business only for now (extend later).
export function isProjectChatBotConnector(con) {
    if (!con || con.isDraft) return false
    const channelType = con.channelType || ''
    const providerType = con.providerType || ''
    if (channelType === CHANNEL_TYPES.maxBusiness) return true
    if (channelType === CHANNEL_TYPES.maxChannel && providerType === PROVIDER_TYPES.maxBusiness) return true
    return providerType === PROVIDER_TYPES.maxBusiness
}

export function getProjectChatBotConnectors(connectors) {
    if (!Array.isArray(connectors)) return []
    return connectors.filter(isProjectChatBotConnector)
}

/** Те же условия, что у пункта «Прирост подписчиков» на странице проекта (без проверки роли). */
export function hasSubscribersGrowthWidgetOnProject(project) {
    if (!project || !Array.isArray(project.connectors)) return false
    const gcb2 = project.connectors.find((c) => c.connectorType === CONNECTOR_TYPES.GCB2)
    if (gcb2?.disabled) return false
    return getProjectChatBotConnectors(project.connectors).length > 0
}

function lsFirstBotKey(projectId) {
    return `${LS_FIRST_BOT_PREFIX}${projectId}`
}

export function syncSubscribersGrowthFirstChatBotAnchor(projectId, connectors) {
    if (!projectId) return
    const bots = getProjectChatBotConnectors(connectors)
    const key = lsFirstBotKey(projectId)
    try {
        if (!bots.length) {
            localStorage.removeItem(key)
            return
        }
        if (localStorage.getItem(key)) return
        const times = bots.map((c) => Number(c.createdAt)).filter((n) => Number.isFinite(n) && n > 0)
        const anchor = times.length ? Math.min(...times) : Date.now()
        localStorage.setItem(key, String(anchor))
    } catch (e) {
    }
}

/** Имя переменной без скобок (для списка вставки; скобки добавляет insertVar). */
export const SUBSCRIBERS_GROWTH_BOT_LINK_VAR = 'ссылка-на-бота'

/** Плейсхолдер только для мастера «Пригласить неподписанных»; не добавлять в общий список переменных MessageSettings. */
export const SUBSCRIBERS_GROWTH_BOT_LINK_PLACEHOLDER = `{${SUBSCRIBERS_GROWTH_BOT_LINK_VAR}}`

export const SAFE_MAILING_SCRIPT_PLACEHOLDER =
    'Замените скрипт активации на актуальное предложение вместо.'

const INVITE_UNSUBSCRIBED_DEFAULT_SCRIPT = `{имя-клиента}, добрый день!
У нас появился официальный бот в МАКСе.
Чтобы беспрепятственно получать от нас напоминания, акции и новости — подпишитесь на нашего бота по ссылке: ${SUBSCRIBERS_GROWTH_BOT_LINK_PLACEHOLDER}`

export function getInviteUnsubscribedDefaultScript() {
    return INVITE_UNSUBSCRIBED_DEFAULT_SCRIPT
}

function normalizeTelegramBotLink(raw) {
    const value = String(raw || '').trim()
    if (!value) return ''
    if (/^https?:\/\//i.test(value)) {
        return value.replace(/\/+$/, '')
    }
    const username = value.replace(/^@/, '').trim()
    if (/^[a-zA-Z0-9_]{3,}$/.test(username)) {
        return `https://t.me/${username}`
    }
    return ''
}

/**
 * Ссылка на MAX-бота из коннектора, выбранного в фильтре виджета (connectorId).
 * Для MAX — формат https://t.me/<username>.
 */
export function getSubscribersGrowthMaxBotTelegramLink(connectorId, connectors = []) {
    if (!connectorId) return ''
    const list = Array.isArray(connectors) ? connectors : []
    const connector = list.find(
        (con) => String(con?.connectorId || con?.id || '') === String(connectorId)
    )
    if (!connector || !isProjectChatBotConnector(connector)) return ''

    const candidates = [
        connector.botTelegramLink,
        connector.telegramBotLink,
        connector.botLink,
        connector.telegramLink,
        connector.data?.botTelegramLink,
        connector.data?.telegramLink,
        connector.data?.botLink,
        connector.data?.username,
        connector.data?.botUsername,
        connector.settings?.botUsername,
        connector.settings?.username,
        connector.messageVars?.botTelegramLink,
        connector.messageVars?.maxBotLink,
        connector.defaultUsername,
        connector.bot?.username,
        connector.bot?.nick,
    ]

    for (const candidate of candidates) {
        const link = normalizeTelegramBotLink(candidate)
        if (link) return link
    }
    return ''
}

export function normalizeSubscribersGrowthBotLink(link) {
    if (link == null) return ''
    if (typeof link === 'string') return link.trim()
    if (typeof link === 'object' && link.data != null) {
        return String(link.data).trim()
    }
    return String(link).trim()
}

export function applySubscribersGrowthBotLinkPlaceholder(text, link) {
    const source = String(text || '')
    if (!source.includes(SUBSCRIBERS_GROWTH_BOT_LINK_PLACEHOLDER)) return source
    const normalizedLink = normalizeSubscribersGrowthBotLink(link)
    return source.split(SUBSCRIBERS_GROWTH_BOT_LINK_PLACEHOLDER).join(normalizedLink)
}

export function restoreSubscribersGrowthBotLinkPlaceholder(text, link) {
    const source = String(text || '')
    const normalizedLink = normalizeSubscribersGrowthBotLink(link)
    if (!normalizedLink || !source.includes(normalizedLink)) return source
    return source.split(normalizedLink).join(SUBSCRIBERS_GROWTH_BOT_LINK_PLACEHOLDER)
}

export function computeSubscribersGrowthShowNewBadge(projectId, connectors) {
    if (!projectId) return false
    const bots = getProjectChatBotConnectors(connectors)
    if (!bots.length) return false
    let raw = ''
    try {
        raw = localStorage.getItem(lsFirstBotKey(projectId)) || ''
    } catch (e) {
        return false
    }
    const anchor = Number(raw)
    if (!Number.isFinite(anchor) || anchor <= 0) return false
    const days = (Date.now() - anchor) / 86400000
    return days <= 30
}

function findConnectorByAnyChannelId(connectors, rawId) {
    const list = Array.isArray(connectors) ? connectors : []
    const id = String(rawId).trim()
    return (
        list.find((con) => String(con?.connectorId || con?.id || '') === id) ||
        list.find(
            (con) => !isBlankChannelId(con?.channelId) && String(con.channelId) === id
        ) ||
        null
    )
}

function isBlankChannelId(value) {
    return value === undefined || value === null || value === ''
}

/**
 * Id канала для extra.sendingSettings / extra.sendChannelId активации.
 *
 * Селекты виджета работают с connectorId из firebase, а активация должна хранить
 * id канала в network-fixer (connector.channelId): именно его кладёт селект
 * «Канал отправки» в настройках активации и по нему бэк ищет канал
 * (channelsService.getChannel). Если сохранить connectorId, канал в активации
 * не находится и селект остаётся пустым.
 */
export function resolveSubscribersGrowthSendingChannelId(rawId, connectors = []) {
    if (isBlankChannelId(rawId)) return ''
    const connector = findConnectorByAnyChannelId(connectors, rawId)
    if (connector && !isBlankChannelId(connector.channelId)) return connector.channelId
    return rawId
}

/**
 * Обратное преобразование: из сохранённого в активации id канала — в connectorId,
 * которым оперируют селекты виджета. Понимает и активации, созданные до фикса,
 * где лежал connectorId.
 */
export function resolveSubscribersGrowthWidgetChannelId(rawId, connectors = []) {
    if (isBlankChannelId(rawId)) return ''
    const connector = findConnectorByAnyChannelId(connectors, rawId)
    if (!connector) return ''
    return String(connector.connectorId || connector.id || '')
}
